/**
 * Single source of truth for turning system status enums + run status codes
 * into human-readable labels and a tone (badge color). Previously three
 * divergent StatusBadge components rendered the raw enum (e.g. "deploy-failed",
 * "consent-pending") and run status was worded three different ways across
 * screens. Everything funnels through here so the same status reads the same
 * everywhere, and a user never sees a raw enum or a bare HTTP code.
 */

const CUSTOMER_LABELS = {
  registered: 'Registered',
  'consent-pending': 'Awaiting consent',
  'consent-confirmed': 'Consent granted',
  deploying: 'Deploying',
  deployed: 'Active',
  active: 'Active',
  'deploy-failed': 'Deployment failed',
  'pending-decommission': 'Decommissioning',
  decommissioned: 'Decommissioned',
  'suspended-trust-lost': 'Suspended (trust role lost)',
  pending: 'Pending',
};


/** Sentence-case a hyphenated enum we don't have an explicit label for. */
function humanize(raw) {
  const s = String(raw).replace(/[-_]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Human-readable label for a customer/deployment status. Never returns a raw enum. */
export function customerStatusLabel(status) {
  if (!status) return 'Unknown';
  return CUSTOMER_LABELS[status] ?? humanize(status);
}

const BADGE_OF_TONE = Object.freeze({ good: 'green', warn: 'amber', bad: 'red' });

/**
 * Badge tone ('green' | 'amber' | 'red' | 'slate') for a customer status. Derived from FLEET_STATUS_TONE, the one
 * status-to-color mapping, so the Customers list, the detail page and the Fleet card cannot diverge (ENTRAID-347):
 * decommissioned and every other failed or removed status is red. A status the map does not name is slate.
 */
export function customerStatusTone(status) {
  return BADGE_OF_TONE[FLEET_STATUS_TONE[status]] ?? 'slate';
}

/**
 * The status dot and tile tone of every customer status the backend can return (portal/backend/lib/customer-status.mjs,
 * STATUS), for the Fleet overview "Active" card (product owner, 2026-10-22): active is good (green), anything on the way
 * to active is caution (amber), and a failed, broken or removed customer is bad (red). Each status is mapped
 * explicitly; customer-status tests fail when the backend adds a status this map does not name.
 */
export const FLEET_STATUS_TONE = Object.freeze({
  deployed: 'good',
  active: 'good',
  registered: 'warn',
  'consent-pending': 'warn',
  'consent-confirmed': 'warn',
  deploying: 'warn',
  'deploy-failed': 'bad',
  'pending-decommission': 'bad',
  decommissioned: 'bad',
  'suspended-trust-lost': 'bad',
});

/** 'good' | 'warn' | 'bad' for a customer status; a missing or unknown status is caution, never green. */
export function fleetStatusTone(status) {
  return FLEET_STATUS_TONE[status] ?? 'warn';
}

const TONE_RANK = Object.freeze({ good: 0, warn: 1, bad: 2 });

/**
 * The Fleet "Active" card's tiles and caption groups: one tile per customer (active first, then caution, then bad) and
 * one group per non-active status label with its count and tone, caution before bad.
 * @returns {{ tiles: string[], groups: Array<{ label: string, count: number, tone: 'warn'|'bad' }> }}
 */
export function fleetStatusSummary(customers) {
  const list = Array.isArray(customers) ? customers : [];
  const tones = list.map((c) => fleetStatusTone(c?.status)).sort((a, b) => TONE_RANK[a] - TONE_RANK[b]);
  const groups = new Map();
  for (const c of list) {
    const tone = fleetStatusTone(c?.status);
    if (tone === 'good') continue;
    const label = customerStatusLabel(c?.status).toLowerCase();
    const g = groups.get(label) ?? { label, count: 0, tone };
    g.count += 1;
    groups.set(label, g);
  }
  return { tiles: tones, groups: [...groups.values()].sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]) };
}

/** Human-readable label for a collector run status code. Never returns a bare code. */
export function runStatusLabel(statusCode) {
  switch (statusCode) {
    case 200: return 'Healthy';
    case 207: return 'Partial';
    case 206: return 'Timed out';
    default: return 'Failed';
  }
}

/** Badge tone for a collector run status code. */
export function runStatusTone(statusCode) {
  if (statusCode === 200) return 'green';
  if (statusCode === 207 || statusCode === 206) return 'amber';
  return 'red';
}
